import { useQuery, useQueries, UseQueryResult } from '@tanstack/react-query'
import { queryKeys } from '../../lib/queryKeys'
import { getMultiOrgGroups, getMultiOrgGroup } from '../../apis/multiOrg'
import { getOrganizationDetails } from '../../apis/organizations'
import type {
  MultiOrgGroup,
  MultiOrgGroupsResponse,
  OrganizationDetails
} from '../../types'

export const useMultiOrgGroups = (): UseQueryResult<MultiOrgGroupsResponse> => {
  return useQuery({
    queryKey: queryKeys.multiOrg.lists(),
    queryFn: async () => {
      const { data } = await getMultiOrgGroups()
      return data
    },
  })
}

export const useMultiOrgGroup = (id: string): UseQueryResult<MultiOrgGroup> => {
  return useQuery({
    queryKey: queryKeys.multiOrg.detail(id),
    queryFn: async () => {
      const { data } = await getMultiOrgGroup(id)
      return data
    },
    enabled: !!id
  })
}

interface MultiOrgMemberDetailsResult {
  memberDetails: (OrganizationDetails | undefined)[]
  isLoading: boolean
  isError: boolean
}

export const useMultiOrgMemberDetails = (
  orgIds: number[],
  enabled = true
): MultiOrgMemberDetailsResult => {
  const queries = useQueries({
    queries: orgIds.map(orgId => ({
      queryKey: queryKeys.organizations.detail(orgId),
      queryFn: async () => {
        const { data } = await getOrganizationDetails(orgId)
        return data
      },
      // One org-stats request per member org, so let callers hold it back until whatever
      // needs it is actually on screen.
      enabled: !!orgId && enabled
    }))
  })

  return {
    memberDetails: queries.map(q => q.data),
    isLoading: queries.some(q => q.isLoading),
    isError: queries.some(q => q.isError)
  }
}
